import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import Iconify from '../../../../reusable-components/iconify';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { postVendorForm } from '../../../../api/invoicing/invoicingVendors';
import { getVendors } from '../../../../redux/slices/invoicing';
import AlertBanner from '../../../../reusable-components/alert-banner/AlertBanner';
import { getInvoiceVendorSchema } from '../../../../reusable-components/form/vendorSchema';

NewVendorModal.propTypes = {
  row: PropTypes.object,
  edit: PropTypes.bool,
};
export default function NewVendorModal({ row = null, edit = false }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [highlightErrors, setHighlightErrors] = useState(false);
  const [debouncedEmailError, setDebouncedEmailError] = useState(null);
  const emailErrorTimerRef = useRef(null);
  const {
    data: {
      invoicingVendors: { vendors, editableVendorBookingTypes },
    },
  } = useSelector((state) => state.invoicing);

  const schema = useMemo(() => getInvoiceVendorSchema(vendors, row), [vendors, row]);
  const defaultValues = useMemo(
    () => ({
      id: edit ? row?.id : '',
      name: edit ? row?.name : '',
      bookingTypeId: edit ? { id: row?.bookingTypeId, name: row?.bookingType } : null,
      code: edit ? row?.code : '',
      subcode: edit ? row?.subcode : '',
      emailTo: edit ? row?.emailTo : '',
      address: edit ? row?.address : '',
      city: edit ? row?.city : '',
      state: edit ? row?.state : '',
      zip: edit ? row?.zip : '',
      phone: edit ? row?.phone : '',
    }),
    [edit, row]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onChange',
  });
  const bookingType = useWatch({ control, name: 'bookingTypeId' });
  const subcodeRequired = bookingType?.name?.toLowerCase().includes('workday') ?? false;
  useEffect(() => {
    clearTimeout(emailErrorTimerRef.current);

    if (!errors.emailTo) {
      setDebouncedEmailError(null);
      return undefined;
    }

    emailErrorTimerRef.current = setTimeout(() => {
      setDebouncedEmailError(errors.emailTo);
    }, 800);

    return () => clearTimeout(emailErrorTimerRef.current);
  }, [errors.emailTo]);

  const visibleErrors = useMemo(() => {
    const merged = { ...errors };
    if (debouncedEmailError) {
      merged.emailTo = debouncedEmailError;
    } else {
      delete merged.emailTo;
    }
    return merged;
  }, [errors, debouncedEmailError]);

  const handleClickOpen = () => {
    reset(defaultValues);
    setOpen(true);
  };
  const handleClose = () => {
    reset();
    setOpen(false);
  };
  const onSubmit = async (formValues) => {
    const response = await postVendorForm(formValues);
    const message = response.data.message ?? 'Error updating Vendor.';
    const idMatch = message.match(/\[Id: (\d+)\]/);
    enqueueSnackbar(message.replace(/\s*\[Id: \d+\]/, '').trim(), {
      variant: response.data.success ? 'success' : 'error',
      persist: response.data.keepFormOpen,
    });
    if (response.data.success && idMatch) {
      dispatch(getVendors([Number(idMatch[1])]));
      handleClose();
    }
  };

  const triggerHighlight = () => {
    setHighlightErrors(true);
    setTimeout(() => setHighlightErrors(false), 1500);
  };
  const errorSx = (fieldName) => ({
    mt: 0.5,
    ...(highlightErrors &&
      visibleErrors[fieldName] && {
        '& .MuiOutlinedInput-root fieldset': {
          borderColor: 'error.main',
          borderWidth: 2,
          transition: 'border-color 0.5s ease',
        },
      }),
  });

  return (
    <>
      {edit ? (
        <IconButton variant="outlined" sx={{ mr: 1 }} size="small" color="secondary" onClick={handleClickOpen}>
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          sx={{ mr: 1 }}
          size="small"
          color="secondary"
          onClick={handleClickOpen}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          New Invoice Vendor
        </Button>
      )}

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          {edit ? 'Edit Invoice Vendor' : 'New Invoice Vendor'}
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>

        <DialogContent>
          <Grid container item xs={12} spacing={2}>
            {/* Booking Type */}
            <Grid item xs={6}>
              <Typography variant="subtitle1">Booking Type *</Typography>
              <Controller
                name="bookingTypeId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    size="small"
                    fullWidth
                    options={editableVendorBookingTypes}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    value={editableVendorBookingTypes?.find((bt) => bt.id === field.value?.id) ?? null}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off" />}
                    sx={{
                      ...{
                        width: '100%',
                        mt: 0.5,
                        '& legend': { display: 'none' },
                        '& fieldset': { top: 0 },
                        '& .MuiFormLabel-root': { display: 'none' },
                      },
                      ...errorSx('bookingTypeId'),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Name */}
            <Grid item xs={6}>
              <Typography variant="subtitle1">Name *</Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-name`}
                    sx={{ ...{ mt: 0.5 }, ...errorSx('name') }}
                  />
                )}
              />
            </Grid>

            {/* Code */}
            <Grid item xs={6}>
              <Typography variant="subtitle1">Vendor Code *</Typography>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField {...field} size="small" fullWidth sx={{ ...{ mt: 0.5 }, ...errorSx('code') }} />
                )}
              />
            </Grid>

            {/* Subcode */}
            <Grid item xs={6}>
              <Typography variant="subtitle1">Vendor Subcode{subcodeRequired ? ' *' : ''}</Typography>
              <Controller
                name="subcode"
                control={control}
                render={({ field }) => (
                  <TextField {...field} size="small" fullWidth sx={{ mt: 0.5, ...errorSx('subcode') }} />
                )}
              />
            </Grid>
            {isDirty && Object.keys(visibleErrors).length > 0 && (
              <Grid item xs={12}>
                <AlertBanner
                  color="error"
                  title="Please fix the following"
                  subtitle={Object.values(visibleErrors)
                    .map((e) => e.message)
                    .join(' · ')}
                  icon={<ErrorOutlineIcon fontSize="small" sx={{ color: (theme) => theme.palette.error.dark }} />}
                  onClick={triggerHighlight}
                />
              </Grid>
            )}
            {/* Email */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Payment Email Contact</Typography>
              <Controller
                name="emailTo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-emailTo`}
                    sx={{ ...{ mt: 0.5 }, ...errorSx('emailTo') }}
                  />
                )}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Address</Typography>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{ mt: 0.5 }}
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-address`}
                  />
                )}
              />
            </Grid>

            {/* City / State */}
            <Grid item xs={6}>
              <Typography variant="subtitle1">City</Typography>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{ mt: 0.5 }}
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-city`}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1">State</Typography>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{ mt: 0.5 }}
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-state`}
                  />
                )}
              />
            </Grid>

            {/* Zip / Phone */}
            <Grid item xs={3}>
              <Typography variant="subtitle1">Zip</Typography>
              <Controller
                name="zip"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{ mt: 0.5 }}
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-zip`}
                  />
                )}
              />
            </Grid>
            <Grid item xs={9}>
              <Typography variant="subtitle1">Phone Number</Typography>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{ mt: 0.5 }}
                    id={`${edit ? 'edit' : 'new'}-invoice-vendor-phone`}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 1 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150 }}
            onClick={async () => {
              const isValid = await trigger();
              if (!isValid) {
                triggerHighlight();
                return;
              }
              await handleSubmit(onSubmit)();
            }}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
